import { ActionMenu, Colors, Icon, Pills } from '@amelio/ui-library';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ReviewQuestion } from '@/types/review/ReviewQuestion';

import { QuestionForm } from '@/pages/shared/QuestionnaireCreation/components/QuestionForm/QuestionForm';
import { getLocalizedText } from '@/utils/localization';
import { getQuestionTypeOptions } from '@/utils/question';

interface QuestionEditFormProps {
    question: ReviewQuestion;
    onSave: (updatedQuestion: ReviewQuestion) => boolean;
    onDelete: () => void;
    onToggleActive: () => void;
    isFrozen?: boolean;
    isPendingUpdate?: boolean;
    isEditing?: boolean;
    onEditStart?: () => void;
    onEditCancel?: () => void;
}

export default function QuestionEditForm({
    question,
    onSave,
    onDelete,
    onToggleActive,
    isFrozen = false,
    isPendingUpdate = false,
    isEditing: isEditingProp,
    onEditStart,
    onEditCancel,
}: QuestionEditFormProps) {
    const { t } = useTranslation();

    const questionTypeLabel = useMemo(() => {
        return (
            getQuestionTypeOptions(t).find((option) => option.value === question.questionType)
                ?.label ?? question.questionType
        );
    }, [t, question.questionType]);
    const [localIsEditing, setLocalIsEditing] = useState(!question.id);
    const [editedQuestion, setEditedQuestion] = useState<ReviewQuestion>({ ...question });

    const isControlled = isEditingProp !== undefined;
    const isEditing = isControlled ? isEditingProp : localIsEditing;

    const handleSave = (updatedQuestion: ReviewQuestion) => {
        const success = onSave(updatedQuestion);
        if (success && !isControlled) {
            setLocalIsEditing(false);
        }
    };

    const handleStartEditing = () => {
        setEditedQuestion({ ...question });
        if (onEditStart) {
            onEditStart();
        } else {
            setLocalIsEditing(true);
        }
    };

    const handleToggleActive = () => {
        onToggleActive();
    };

    const handleDelete = () => {
        if (isFrozen) {
            return;
        }
        onDelete();
        if (!question.id) {
            setLocalIsEditing(false);
        }
    };

    const menuItems = [
        {
            id: 'edit',
            label: t('visionValues.edit'),
            onClick: handleStartEditing,
            icon: (
                <Icon
                    name={'edit-edit-2'}
                    size={'sm'}
                />
            ),
        },
        {
            id: 'toggle-active',
            label:
                question.status === 'Active'
                    ? t('visionValues.deactivate')
                    : t('visionValues.activate'),
            onClick: handleToggleActive,
            icon: (
                <Icon
                    name={
                        question.status === 'Active'
                            ? 'electronics-power-off'
                            : 'electronics-power-on'
                    }
                    size={'sm'}
                />
            ),
        },
        {
            id: 'delete',
            label: t('visionValues.delete'),
            onClick: handleDelete,
            disabled: isFrozen,
            icon: (
                <Icon
                    name={'essential-trash'}
                    size={'sm'}
                    color={Colors.CHART_RED}
                />
            ),
            danger: true,
        },
    ];

    if (isEditing) {
        return (
            <div className={'rounded-xl border border-gray-100 bg-white p-4 shadow-md'}>
                <QuestionForm
                    mode={editedQuestion.id ? 'edit' : 'add'}
                    question={editedQuestion}
                    onQuestionChange={setEditedQuestion}
                    onCancel={() => {
                        if (!question.id) {
                            onDelete();
                        } else {
                            if (isControlled && onEditCancel) {
                                onEditCancel();
                            } else {
                                setLocalIsEditing(false);
                            }
                            setEditedQuestion({ ...question });
                        }
                    }}
                    onSave={handleSave}
                    isPendingUpdateQuestion={isPendingUpdate}
                    readOnly={isFrozen}
                />
            </div>
        );
    }

    return (
        <div
            className={
                'flex w-full cursor-pointer items-center justify-between rounded-lg bg-gray-50 p-3 hover:bg-gray-100'
            }
            onClick={handleStartEditing}
        >
            <div className={'flex-1'}>
                <p className={'text-navy text-body-sm leading-[18px] font-normal'}>
                    {getLocalizedText(question.label) || questionTypeLabel}
                </p>

                <p className={'text-caption mt-1 text-gray-500'}>{questionTypeLabel}</p>
            </div>

            <div className={'flex items-center gap-2'}>
                <Pills
                    variant={question.status === 'Active' ? 'activeLight' : 'inactiveLight'}
                    status={question.status === 'Active' ? 'active' : 'inactive'}
                    size={'sm'}
                >
                    {question.status === 'Active'
                        ? t('visionValues.active')
                        : t('visionValues.inactive')}
                </Pills>

                <div onClick={(e) => e.stopPropagation()}>
                    <ActionMenu
                        items={menuItems}
                        ariaLabel={t('visionValues.moreOptions')}
                        variant={'subtle'}
                        size={'sm'}
                        className={'border-0'}
                    />
                </div>
            </div>
        </div>
    );
}
